import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useTenant } from '../../context/TenantContext';
import { focalPos } from '../course-builder/slides/FocalPointPicker';
import { useMarketingCourses } from './MarketingData';
import { inlineEm } from './inlineEm';
import { onThumbError } from '../ui/imageFallback';

const DEFAULTS = {
  headline: 'Transform your skills with *expert-led* courses',
  subtitle:
    'Practical, hands-on learning designed for busy professionals. Master new skills at your own pace with courses that deliver real results.',
  ctaText: 'Browse the catalogue',
  ctaHref: '/courses',
};

/** Editorial hero. Headline supports `*word*` markdown for italic emphasis. */
export const Hero: React.FC = () => {
  const { tenant } = useTenant();
  const hero = tenant?.branding.marketing?.hero ?? {};
  const headline = hero.headline || DEFAULTS.headline;
  const subtitle = hero.subtitle || DEFAULTS.subtitle;
  const ctaText = hero.ctaText || DEFAULTS.ctaText;
  const ctaHref = hero.ctaHref || DEFAULTS.ctaHref;
  const imageUrl = hero.imageUrl;
  const eyebrow = hero.eyebrow?.trim();

  const { courses, loaded } = useMarketingCourses();
  const courseCount = courses.length;

  return (
    <section id="hero" className="relative bg-white border-b border-gray-200">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10 md:pt-20 md:pb-14">
        <div
          className={`grid gap-10 md:gap-14 items-end ${
            imageUrl ? 'md:grid-cols-[1.15fr_0.95fr]' : 'md:grid-cols-1 max-w-4xl'
          }`}
        >
          {/* Image — appears ABOVE text on mobile */}
          {imageUrl && (
            <figure className="relative aspect-[4/5] bg-gray-100 overflow-hidden md:order-last order-first">
              <img
                src={imageUrl}
                onError={onThumbError}
                alt=""
                loading="eager"
                fetchPriority="high"
                className="w-full h-full object-cover"
                style={{ objectPosition: focalPos(hero.imagePosition), filter: 'saturate(0.94) contrast(1.02)' }}
              />
              <figcaption className="absolute bottom-3 left-3 right-3 flex justify-between font-mono text-[10px] uppercase tracking-[0.12em] text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                <span>From the studio</span>
                <span>Plate I</span>
              </figcaption>
            </figure>
          )}

          {/* Text content */}
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-gray-500 mb-7 flex items-center gap-3.5">
              <span className="text-primary-600">Nº 01</span>
              <span className="flex-1 max-w-[80px] h-px bg-gray-900 opacity-45" />
              {eyebrow ? <span>{eyebrow}</span> : <span>An editorial school</span>}
            </div>

            <h1 className="font-serif text-[44px] sm:text-5xl md:text-6xl lg:text-[88px] leading-[0.94] tracking-[-0.03em] mb-7 text-balance text-gray-900">
              {inlineEm(headline)}
            </h1>

            <p className="text-lg md:text-xl text-gray-600 mb-9 leading-[1.5] max-w-[44ch]">
              {subtitle}
            </p>

            <div className="flex items-center gap-6 flex-wrap">
              <Link
                to={ctaHref}
                className="group inline-flex items-center gap-3 px-7 py-3.5 bg-gray-900 text-white text-base font-semibold rounded-full hover:opacity-90 transition-all duration-200 hover:-translate-y-0.5"
              >
                {ctaText}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/login"
                className="font-mono text-[11px] uppercase tracking-[0.12em] pb-1 border-b border-gray-900 hover:text-primary-700 hover:border-primary-700 transition-colors"
              >
                How it works
              </Link>
            </div>
          </div>
        </div>

        {/* Ticker bar — real signals only */}
        <div
          className="mt-12 border-t border-b border-gray-900 py-3.5 flex items-center gap-x-7 gap-y-2 flex-wrap font-mono text-[11px] uppercase tracking-[0.12em] text-gray-500 transition-opacity duration-500"
          style={{ opacity: loaded ? 1 : 0 }}
        >
          <span>
            <strong className="text-gray-900 font-medium">{courseCount}</strong>{' '}
            {courseCount === 1 ? 'programme' : 'programmes'}
          </span>
          <span className="text-primary-600">/</span>
          <span>Self-paced, with cohort intakes</span>
          <span className="text-primary-600">/</span>
          <span>Practitioner faculty</span>
          <span className="text-primary-600 ml-auto hidden sm:inline">/</span>
          <span className="hidden sm:inline">↓ Read on</span>
        </div>
      </div>
    </section>
  );
};
